import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FileItem } from 'ng2-file-upload';
import { Util } from 'app/models/util';
import { FileUploadOptions } from 'app/models/document';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DuplicateFileComponent, duplicateFileData } from 'app/modules/admin/apps/file-manager/modal/duplicate-file/duplicate-file.component';
import { ToastrService } from 'ngx-toastr';
import { LpmCopilotConfig } from 'app/models/lpm.config';
import { DmsFileItem, DmsFileUploader } from 'app/models/dms-file-item';

@Injectable({
    providedIn: "root",
  })
export class FileUploadQueueService {

    // ── Reactive state ──────────────────────────────────────────────────────────
    private _queue$      = new BehaviorSubject<DmsFileItem[]>([]);
    private _isVisible$  = new BehaviorSubject<boolean>(false);
    private _header$     = new BehaviorSubject<string>('Upload Queue');
    private _isPreparing$ = new BehaviorSubject<boolean>(false);

    /** list of queued FileItems. */
    queue$: Observable<DmsFileItem[]>  = this._queue$.asObservable();
    // upload popup status.
    isVisible$: Observable<boolean> = this._isVisible$.asObservable();
    // header title for the popup, e.g. "Uploading 3 items".
    queueHeader$: Observable<string> = this._header$.asObservable();
    // true while a dropped/picked folder is being traversed, before any file has reached the queue.
    isPreparing$: Observable<boolean> = this._isPreparing$.asObservable();

    pendingExistFiles: DmsFileItem[] = [];
    private isDuplicateDialogOpen = false;

    uploader: DmsFileUploader | null = null;
    currentFolderId: string | null = null;

    copilotConfig: LpmCopilotConfig | null = null;

    // ── Event Emitters ─────────────────────────────────────────────────────────
    public onCompleteItem$ = new Subject<{item: DmsFileItem, response: any, status: any, headers: any}>();
    public onSuccessItem$  = new Subject<{item: DmsFileItem, response: any, status: any, headers: any}>();
    public onCompleteAll$  = new Subject<void>();

    constructor(
        private util: Util,
        private ngZone: NgZone,
        private _matDialog: MatDialog,
        private toastr: ToastrService,
    ) {
        window.addEventListener('beforeunload', (event) => {
            if (this.uploader?.isUploading) {
                event.preventDefault();
                event.returnValue = '';
            }
        });
    }

    // public methods

    initUploader(options: FileUploadOptions): DmsFileUploader {
        const folderId = options.folderId || this.currentFolderId;
        this.currentFolderId = folderId;
        if (!this.uploader) {
            this.uploader = this.util.fileUpload(options);
            this.util.fileUploader = this.uploader;
            // Mirror queue changes into the observable
            this.uploader.onAfterAddingAll = () => this._pushQueue();
            this.uploader.onAfterAddingFile = (fileItem: FileItem) => {
                const item = fileItem as unknown as DmsFileItem;
                item.url = this.util.getFileUploadUrl(this.currentFolderId);
                item.some.id = crypto.randomUUID();
                item.some.title = 'In Queue';
                this._isPreparing$.next(false);
                this._pushQueue();
                this.updateQueueHeader();
            };
            this.uploader.onProgressItem = () => {
                this._pushQueue();
                this.updateQueueHeader();
            };
            this.uploader.onCompleteItem = (fileItem: FileItem, response, status, headers) =>
                this._handleItemComplete(fileItem as unknown as DmsFileItem, response, status, headers);

            this.uploader.onSuccessItem = (fileItem: FileItem, response, status, headers) => {
                const item = fileItem as unknown as DmsFileItem;
                this.onSuccessItem$.next({item, response, status, headers});
            };
            this.uploader.onCompleteAll = () => {
                this.promptUserForExistingFiles();
                this.onCompleteAll$.next();
            };
            this.uploader._onErrorItem = (fileItem: FileItem, response) =>
                this._handleNetworkError(fileItem as unknown as DmsFileItem, response);
            this.uploader.onCancelItem = () => {
                this._pushQueue();
                this.updateQueueHeader();
            };
        }
        return this.uploader;
    }

    /**
     * Show the upload popup (sets `isVisible$` to true).
     */
    show(): void {
        this._isVisible$.next(true);
    }

    /**
     * Opens the popup in a "preparing" state
     */
    startPreparing(): void {
        this._isPreparing$.next(true);
        this._isVisible$.next(true);
    }

    stopPreparing(): void {
        this._isPreparing$.next(false);
    }

    /**
     * Hides the popup and optionally clears the queue.
     */
    dismiss(clearQueue = false): void {
        this._isVisible$.next(false);
        if (clearQueue) {
            this.pendingExistFiles = [];
            if (this.uploader) {
                this.uploader.queue = [];
                this._pushQueue();
            }
        }
    }

    /**
     * cancel upload item
     */
    public cancelItem(file: DmsFileItem): void {
        if (!file) return;
        try {
            file.cancel();
        } catch (e) {
            console.error("Error cancelling item", e);
        }
        this.updateQueueHeader();
    }

    /**
     * remove item from the queue
     */
    public removeItem(file: DmsFileItem): void {
        if (!file || !this.uploader) return;

        const index = this.uploader.queue.indexOf(file);
        if (index === -1) {
            this._pushQueue();
            this.updateQueueHeader();
            return;
        }

        try {
            file.remove();
        } catch (e) {
            console.warn("Manual queue splice due to ng2-file-upload error", e);
            this.uploader.queue.splice(index, 1);
        }

        this._pushQueue();
        this.updateQueueHeader();
    }

    /**
     * Recalculates queue header.
     */
    updateQueueHeader(): void {
        const queue = this.uploader?.queue ?? [];
        const total = queue.length;

        if (!total) {
            this._header$.next('Upload Queue');
            this.dismiss();
            return;
        }

        const failed    = queue.filter(i => i.isError).length;
        const succeeded = queue.filter(i => i.isSuccess).length;
        const uploading = queue.filter(i => i.isUploading).length;
        const remaining = total - succeeded - failed - uploading;
        const settled   = succeeded + failed;
        const inProgress = uploading > 0 || remaining > 0;
        const noun = total === 1 ? 'file' : 'files';

        let header: string;
        if (inProgress) {
            header = total === 1
                ? 'Uploading 1 file'
                : `Uploading ${settled} of ${total} files`;
            if (failed > 0) header += ` · ${failed} failed`;
        } else if (failed > 0) {
            header = succeeded > 0
                ? `${succeeded} uploaded, ${failed} failed`
                : `${failed} ${noun} failed to upload`;
        } else {
            header = `${succeeded} ${noun} uploaded`;
        }

        this.ngZone.run(() => {
            this._header$.next(header);
            this._pushQueue();
        });
    }

    promptUserForExistingFiles(): void {
        if (!this.pendingExistFiles?.length) return;
        // avoid stacking duplicate dialogs
        if (this.isDuplicateDialogOpen) return;

        const frozenFolderId = this.currentFolderId;

        const dialogConfig: MatDialogConfig<duplicateFileData> = {
            data: {
                item: this.pendingExistFiles[0],
                routeFolderId: frozenFolderId,
                existingFiles: this.pendingExistFiles,
                copilotConfig: this.copilotConfig,
            }
        };

        this.isDuplicateDialogOpen = true;
        const dialogRef = this._matDialog.open(DuplicateFileComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result: DmsFileItem[]) => {
            this.isDuplicateDialogOpen = false;
            this.pendingExistFiles = result ?? [];
            if (result?.length) {
                this.uploader?.queue.forEach((queueItem: DmsFileItem) => {
                    result.forEach((dupItem: DmsFileItem) => {
                        if (dupItem.some.id === queueItem.some.id && !queueItem.isSuccess) {
                            if (!dupItem.isCancel && queueItem.isCancel !== true) {
                                queueItem.url = dupItem.url;
                                queueItem.isUploading = false;
                                queueItem.isUploaded  = false;
                                queueItem.onComplete  = () => {
                                    this.pendingExistFiles = this.pendingExistFiles.filter(
                                        f => f.some.id !== queueItem.some.id
                                    );
                                    this._showCallBackToastr(queueItem);
                                };
                                queueItem.some.title = 'Uploading...';
                                queueItem.upload();
                            } else {
                                this.pendingExistFiles = this.pendingExistFiles.filter(
                                    f => f.some.id !== queueItem.some.id
                                );
                                queueItem.some.title = queueItem.some.error_message + ' - ' + queueItem.some.duplicate_action;
                            }
                        }
                    });
                });
            } else {
                this.onCompleteAll$.next();
            }
        });
    }

    private _handleNetworkError(item: DmsFileItem, response: string): void {
        item.isError   = true;
        item.isSuccess = false;
        item.cancel();

        let message = 'Upload Failed';
        try {
            const error = JSON.parse(response);
            message = error?.detail ?? message;
        } catch { /* non-JSON network error */ }

        item.some.error_message = message;
        item.some.title = message;
        this.toastr.error(message, 'Upload Error');
        this._pushQueue();
        this.updateQueueHeader();
    }

    private _handleItemComplete(item: DmsFileItem, response: string, status: any, headers: any): void {
        let res: any;
        try {
            res = JSON.parse(response);
        } catch {
            this.toastr.info('File upload terminated', 'Upload Warning');
            return;
        }

        const failed       = res.status === 'ERROR';
        const alreadyExists = res?.error?.code === '900';

        item.isError   = failed;
        item.isSuccess = !failed;

        if (failed) {
            const message = alreadyExists
                ? 'File already exists'
                : `Upload failed: ${res?.error?.message ?? 'Unknown error'}`;
            item.some.error_message = message;
            item.some.title = message;
            if (alreadyExists) {
                this.pendingExistFiles.push(item);
            } else {
                this.toastr.error(message, 'Upload Error');
            }
        } else {
            const savedName = res?.data?.name ?? item.file.name;
            item.some.savedName = savedName;
            item.some.title = item.some.duplicate_action
                ? `uploaded successfully (${item.some.duplicate_action} as ${savedName})`
                : 'uploaded successfully';
            this.onCompleteItem$.next({ item, response, status, headers });
        }

        this._pushQueue();
        this.updateQueueHeader();
    }

    _showCallBackToastr(item: DmsFileItem): void {
        let message: string;
        switch (true) {
            case item.url.includes('copy'):
                message = 'File Copied!';
                break;
            case item.url.includes('replace'):
                message = 'File Overwritten!';
                break;
            default:
                message = 'Uploaded Successfully';
                return;
        }
        this.toastr.success(message, 'Success');
    }

    private _pushQueue(): void {
        this.ngZone.run(() => {
            this._queue$.next([...(this.uploader?.queue ?? [])]);
        });
    }
}
